import React, { useEffect, useState } from 'react';
import {
  CalendarIcon,
  HandsClappingIcon,
  UserGroupIcon,
  PlayIcon,
} from '@revfluence/fresh-icons/solid/esm';
import { Carousel, CarouselContent, CarouselItem } from '@frontend/shadcn/components/ui/carousel';
import type { UseEmblaCarouselType } from 'embla-carousel-react';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { MemberSearchQuery_members } from '@frontend/app/queries/types/MemberSearchQuery';
import { format } from 'date-fns';
import Empty from '@frontend/app/refresh-components/Empty';
import { toast } from '@frontend/shadcn/hooks/use-toast';
import { ISocialAccount } from '@components';
import { Checkbox } from '@/shadcn/components/ui/checkbox';
import { Avatar, AvatarImage, AvatarFallback } from '@/shadcn/components/ui/avatar';
import { Badge } from '@/shadcn/components/ui/badge';
import { ProjectsPageState } from '../../constants';
import { ApplicantOperationButtons } from '../TableHeader/ApplicantOperationButtons';
import { TProject } from '../../types';
import { formatNumber } from './utils/formatNumber';
import { PostPreviewUrls } from './types';
import { MediaItem } from './MediaItem';
import { ProspectsOverview } from './ProspectsOverview';
import { useProspectsListContext } from './ProspectsListContext';

export interface ProspectCardProps {
  isSelected?: boolean;
  onSelect?: (id: number, selected: boolean) => void;
  instagramSchemaId?: number;
  tiktokSchemaId?: number;
  member: MemberSearchQuery_members;
  pageState: ProjectsPageState;
  project: TProject;
  refetchData: () => void;
  allMembers?: MemberSearchQuery_members[];
}

export const ProspectCard: React.FC<ProspectCardProps> = ({
  isSelected = false,
  onSelect,
  instagramSchemaId,
  tiktokSchemaId,
  member,
  pageState,
  project,
  refetchData,
  allMembers = [],
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [carouselApi, setCarouselApi] = useState<UseEmblaCarouselType[1] | null>(null);

  const [hasSocialData, setHasSocialData] = useState(false);

  // Overview drawer state
  const [isOverviewOpen, setIsOverviewOpen] = useState(false);

  const [username, setUsername] = useState<string | null>(null);
  const [fullName, setFullName] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [followers, setFollowers] = useState<number | null>(null);
  const [engagement, setEngagement] = useState<number | null>(null);
  const [postPreviewUrls, setPostPreviewUrls] = useState<PostPreviewUrls[]>([]);
  const [socialData, setSocialData] = useState<ISocialAccount | null>(null);

  const { socialProfileMap, isFetchingSocialData } = useProspectsListContext();

  const handleDotClick = (index: number) => {
    setCurrentImageIndex(index);
    carouselApi?.scrollTo(index);
  };

  const handleProfileClick = () => {
    setIsOverviewOpen(true);
  };

  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    const onSelect = () => {
      setCurrentImageIndex(carouselApi.selectedScrollSnap());
    };

    carouselApi.on('select', onSelect);
    onSelect(); // Set initial index

    return () => {
      carouselApi?.off('select', onSelect);
    };
  }, [carouselApi]);

  const instagramUsername = member.fields[instagramSchemaId];
  const tiktokUsername = member.fields[tiktokSchemaId];

  useEffect(() => {
    if (instagramUsername || tiktokUsername) {
      const socialProfile = socialProfileMap[instagramUsername || tiktokUsername];
      if (!socialProfile) {
        setHasSocialData(false);
        return;
      }
      const socialProfileData: ISocialAccount = socialProfile;
      setSocialData(socialProfileData); // Store full social data for overview
      setUsername(socialProfileData.username);
      setFullName(socialProfileData.full_display_name);
      setAvatarUrl(socialProfileData.profile_picture);
      setFollowers(socialProfileData.follower_count);
      setEngagement(socialProfileData.engagement);

      // Check multiple sources for post preview URLs, similar to Posts.tsx pattern
      let foundPostUrls: PostPreviewUrls[] = [];

      // 1. First priority: mentioned_posts (if available)
      if (socialProfileData?.mentioned_posts?.length) {
        const mentionedPosts = socialProfileData.mentioned_posts
          .filter((post) => post && (post.image || post.video))
          .slice(0, 5); // Limit to 5 posts for carousel

        if (mentionedPosts.length > 0) {
          foundPostUrls = mentionedPosts.map((post) => ({
            url: post.image || post.video,
            media_type: post.image ? 'image' : 'video',
          }));
        }
      }

      // 2. Second priority: popular_posts from IQ data (if no mentioned posts)
      if (foundPostUrls.length === 0 && socialProfileData?.popular_posts?.length) {
        const popularPosts = socialProfileData.popular_posts
          .filter((post) => post && (post.image || post.video))
          .slice(0, 5); // Limit to 5 posts for carousel

        if (popularPosts.length > 0) {
          foundPostUrls = popularPosts.map((post) => ({
            url: post.image || post.video,
            media_type: post.image ? 'image' : 'video',
          }));
        }
      }

      // 3. Third priority: sponsored_posts (for Instagram)
      if (foundPostUrls.length === 0 && socialProfileData?.sponsored_posts?.length) {
        const sponsoredPosts = socialProfileData.sponsored_posts
          .filter((post) => post && (post.image || post.video))
          .slice(0, 5); // Limit to 5 posts for carousel

        if (sponsoredPosts.length > 0) {
          foundPostUrls = sponsoredPosts.map((post) => ({
            url: post.image || post.video,
            media_type: post.image ? 'image' : 'video',
          }));
        }
      }

      // 4. Fourth priority: regular posts sorted by view count (most popular)
      if (foundPostUrls.length === 0 && socialProfileData?.posts?.length) {
        const sortedPosts = socialProfileData.posts
          .filter((post) => post && (post.image || post.video))
          .sort((a, b) => (b.view_count || 0) - (a.view_count || 0))
          .slice(0, 5); // Limit to 5 posts for carousel

        if (sortedPosts.length > 0) {
          foundPostUrls = sortedPosts.map((post) => ({
            url: post.image || post.video,
            media_type: post.image ? 'image' : 'video',
          }));
        }
      }

      // 5. Fifth priority: recent posts (chronological)
      if (foundPostUrls.length === 0 && socialProfileData?.posts?.length) {
        const recentPosts = socialProfileData.posts
          .filter((post) => post && (post.image || post.video))
          .sort((a, b) => (b.ts || 0) - (a.ts || 0))
          .slice(0, 5); // Limit to 5 posts for carousel

        if (recentPosts.length > 0) {
          foundPostUrls = recentPosts.map((post) => ({
            url: post.image || post.video,
            media_type: post.image ? 'image' : 'video',
          }));
        }
      }

      // 6. Last fallback: posts media array (existing logic)
      if (foundPostUrls.length === 0 && socialProfileData.posts?.[0]?.media?.length) {
        foundPostUrls = socialProfileData.posts[0].media
          .slice(0, 5) // Limit to 5 posts for carousel
          .map((media) => ({
            url: media.url,
            media_type: media.media_type as 'image' | 'video',
          }));
      }

      setPostPreviewUrls(foundPostUrls);
      setHasSocialData(true);
    } else {
      setHasSocialData(false);
    }
  }, [instagramUsername, socialProfileMap, tiktokUsername]);

  // Empty state component for no social data
  const EmptyState = () => (
    <div className="w-64 h-[462px] rounded-2xl border border-grey-2 flex flex-col gap-2 overflow-hidden relative bg-grey-1">
      {/* Background placeholder - matches the carousel area */}
      <div className="absolute inset-0 w-full z-2">
        <div className="w-full h-[462px] flex flex-col justify-center items-center">
          <Empty
            icon={UserGroupIcon}
            title="No Social Account"
            description="No Instagram or TikTok account found"
            size="md"
          />
        </div>
      </div>

      {/* Top section with date and checkbox - matches social card */}
      <div className="px-3 pt-3 flex justify-between items-center z-10">
        <Badge variant="secondary" className="bg-grey-5 text-white">
          <CalendarIcon className="h-3 w-3 mr-1" />
          {format(new Date(member.createdDate), 'MMM d, yyyy')}
        </Badge>
        <Checkbox
          checked={isSelected}
          onCheckedChange={(checked) => onSelect?.(member.id, !!checked)}
          className="bg-white"
        />
      </div>

      {/* Bottom section - identical structure to social card */}
      <div className="mt-auto flex flex-col justify-center items-center gap-2 z-10">
        <div className="w-full p-3 bg-gradient-to-b from-white/90 to-white/90 rounded-2xl flex flex-col gap-2 z-10">
          <div
            className="flex items-start gap-2 cursor-pointer hover:bg-grey-2/50 rounded-lg p-2 -m-2 transition-colors"
            onClick={handleProfileClick}
          >
            <Avatar className="h-10 w-10">
              <AvatarFallback className="bg-grey-4 text-grey-6">
                {member.name
                  ?.split(' ')
                  .map((n) => n[0])
                  .join('') || 'N/A'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 flex flex-col">
              <div className="text-grey-6 text-xs font-medium leading-tight break-all">
                {member.email || 'No email provided'}
              </div>
              <div className="text-grey-6 text-xs font-normal leading-tight break-all">
                {member.name || 'No name provided'}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <div className="w-5 h-5 flex justify-center items-center">
                  <UserGroupIcon fontSize={14} className="text-blue-5" />
                </div>
                <div className="text-grey-6 text-base font-medium leading-7">-</div>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-5 h-5 flex justify-center items-center">
                  <HandsClappingIcon fontSize={14} className="text-pink-5" />
                </div>
                <div className="text-grey-6 text-base font-medium leading-7">-</div>
              </div>
            </div>
          </div>

          {[ProjectsPageState.Applicants, ProjectsPageState.Maybe].includes(pageState) && (
            <ApplicantOperationButtons
              hideMaybeButton={pageState === ProjectsPageState.Maybe}
              memberIds={[member.id]}
              onError={(reason) => {
                toast({
                  title: 'Error',
                  description: reason || 'Failed to perform operation',
                });
              }}
              project={project}
              showFlexUI
              showInCard
              onMemberApplicantOperationSuccess={() => {
                refetchData();
              }}
            />
          )}
        </div>
      </div>

      {/* Prospects Overview Drawer */}
      <ProspectsOverview
        open={isOverviewOpen}
        onOpenChange={setIsOverviewOpen}
        member={member}
        isLoading={isFetchingSocialData}
        socialData={socialData}
        isFetchingSocialData={isFetchingSocialData}
        pageState={pageState}
        project={project}
        refetchData={refetchData}
        allMembers={allMembers}
      />
    </div>
  );

  // Show loading skeleton while fetching
  if (isFetchingSocialData) {
    return (
      <div className="w-64 h-[462px] rounded-2xl border border-grey-2 flex justify-center items-center">
        <Skeleton className="w-full h-[462px] rounded-2xl border border-grey-2" />
      </div>
    );
  }

  // Show empty state if no social data is available
  if (!hasSocialData) {
    return <EmptyState />;
  }

  return (
    <div className="w-64 h-[462px] rounded-2xl border border-grey-2 flex flex-col gap-2 overflow-hidden relative bg-grey-1">
      <Carousel
        opts={{
          loop: true,
          dragFree: false,
        }}
        setApi={setCarouselApi}
        className="absolute inset-0 w-full z-2"
      >
        <CarouselContent>
          {postPreviewUrls?.map((post, index) => (
            <CarouselItem key={index}>
              <MediaItem post={post} />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
      <div className="px-3 pt-3 flex justify-between items-center z-10">
        <Badge variant="secondary" className="bg-grey-5 text-white">
          <CalendarIcon className="h-3 w-3 mr-1" />
          {format(new Date(member.createdDate), 'MMM d, yyyy')}
        </Badge>
        <Checkbox
          checked={isSelected}
          onCheckedChange={(checked) => onSelect?.(member.id, !!checked)}
          className="bg-white"
        />
      </div>

      <div className="mt-auto flex flex-col justify-center items-center gap-2 z-10">
        {postPreviewUrls?.length > 1 && (
          <div className="flex justify-center items-center gap-1">
            {postPreviewUrls?.map((_, index) => (
              <button
                key={index}
                onClick={() => handleDotClick(index)}
                className={`w-1 h-1 rounded-full transition-opacity cursor-pointer ${
                  index === currentImageIndex ? 'bg-white opacity-100' : 'bg-white opacity-50 hover:opacity-75'
                }`}
              />
            ))}
          </div>
        )}

        {!postPreviewUrls?.length && (
          <div className="absolute inset-0 w-full z-2">
            <div className="w-full h-[462px] flex flex-col justify-center items-center">
              <Empty
                icon={PlayIcon}
                title="No Media Available"
                description="This account has no accessible images or videos"
                size="md"
              />
            </div>
          </div>
        )}

        <div className="w-full p-3 bg-gradient-to-b from-white/90 to-white/90 rounded-2xl flex flex-col gap-2 z-10">
          <div
            className="flex items-start gap-2 cursor-pointer hover:bg-grey-2/50 rounded-lg p-2 -m-2 transition-colors"
            onClick={handleProfileClick}
          >
            <Avatar className="h-10 w-10">
              <AvatarImage src={avatarUrl} alt={fullName} />
              <AvatarFallback className="bg-grey-4 text-grey-6">
                {fullName
                  ?.split(' ')
                  .map((n) => n[0])
                  .join('')}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 flex flex-col">
              <div className="text-grey-6 text-xs font-medium leading-tight break-all">{username}</div>
              <div className="text-grey-6 text-xs font-normal leading-tight break-all">{fullName}</div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <div className="w-5 h-5 flex justify-center items-center">
                  <UserGroupIcon fontSize={14} className="text-blue-5" />
                </div>
                <div className="text-grey-6 text-base font-medium leading-7">{formatNumber(followers)}</div>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-5 h-5 flex justify-center items-center">
                  <HandsClappingIcon fontSize={14} className="text-pink-5" />
                </div>
                <div className="text-grey-6 text-base font-medium leading-7">{formatNumber(engagement)}</div>
              </div>
            </div>
          </div>

          {[ProjectsPageState.Applicants, ProjectsPageState.Maybe].includes(pageState) && (
            <ApplicantOperationButtons
              hideMaybeButton={pageState === ProjectsPageState.Maybe}
              memberIds={[member.id]}
              onError={(reason) => {
                toast({
                  title: 'Error',
                  description: reason || 'Failed to perform operation',
                });
              }}
              project={project}
              showFlexUI
              showInCard
              onMemberApplicantOperationSuccess={() => {
                refetchData();
              }}
            />
          )}
        </div>
      </div>

      {/* Prospects Overview Drawer */}
      <ProspectsOverview
        open={isOverviewOpen}
        onOpenChange={setIsOverviewOpen}
        member={member}
        isLoading={isFetchingSocialData}
        socialData={socialData}
        isFetchingSocialData={isFetchingSocialData}
        pageState={pageState}
        project={project}
        refetchData={refetchData}
        allMembers={allMembers}
      />
    </div>
  );
};
